import type { EventRecord } from '@zrc/core';
import { zoomContextHeaders } from './zoomContext.js';

export interface StoredEvent {
  eventId: string;
  revision: number;
  record: EventRecord;
  updatedAt: string;
  /** Who currently holds control of the event, if anyone. */
  lease?: { holder: string; expiresAt: string } | null;
}

/**
 * Backend client. The Zoom client attaches X-Zoom-App-Context to every
 * request itself; outside Zoom (simulator mode) the backend accepts a
 * development header when DEV_FAKE_CONTEXT=1.
 */
export interface Me {
  kind: 'admin' | 'operator' | 'dev' | 'anonymous';
  email?: string;
  name?: string;
  userId?: string | null;
  meetingId?: string | null;
  /** Why the server did not recognise the caller, when it said. */
  detail?: string;
}

/** Who is calling. Never throws: an unreachable or refusing backend reads as anonymous, with the reason. */
export async function fetchMe(): Promise<Me> {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin', headers: zoomContextHeaders() });
    if (!res.ok) {
      let reason = '';
      try {
        reason = ((await res.json()) as { error?: string }).error ?? '';
      } catch {
        /* not json */
      }
      return { kind: 'anonymous', detail: `The server answered ${res.status}${reason ? `: ${reason}` : ''}` };
    }
    return (await res.json()) as Me;
  } catch (e) {
    return { kind: 'anonymous', detail: `Could not reach the server: ${e instanceof Error ? e.message : String(e)}` };
  }
}

export interface AccessInfo {
  codes: { id: string; createdAt: string; expiresAt: string; usedAt: string | null }[];
  operators: { zoomUserId: string; boundAt: string }[];
}

export interface Question {
  id: string;
  label: string;
  required: boolean;
}
export interface RoomSetup {
  roomId: string;
  roomName: string;
  capacity: number;
}
export interface EventSetup {
  startsAt: string | null;
  durationMinutes: number;
  timezone: string;
  rooms: RoomSetup[];
  rounds: number;
  roundMinutes: number;
  gapMinutes: number;
  firstRoundOffsetMinutes: number;
  studentQuestions: Question[];
  speakerQuestions: Question[];
  registrationOpen: boolean;
  maxStudents: number;
  meeting: { meetingId: string; joinUrl: string; createdAt: string } | null;
}
export interface SetupResponse {
  setup: EventSetup;
  links: { student: string; speaker: string };
  zoomConfigured: boolean;
}
export interface RegistrationRow {
  id: string;
  kind: 'student' | 'speaker';
  participantId: string;
  displayName: string;
  email: string;
  answers: Record<string, string>;
  roomId: string | null;
  status: string;
  hasLink: boolean;
  joinUrl: string | null;
  createdAt: string;
}
export interface RotaPreview {
  files: { participants: string; rooms: string; rounds: string; assignments: string };
  summary: { seats: Record<string, number>; sitOuts: Record<string, number>; distinctRooms: Record<string, number>; warnings: string[] };
  counts: { students: number; speakers: number; pendingSpeakers: number };
}
export interface PublicRegisterInfo {
  kind: 'student' | 'speaker';
  title: string;
  startsAt: string | null;
  timezone: string;
  open: boolean;
  retentionDays: number;
  questions: Question[];
  rooms: { roomId: string; roomName: string }[];
}

async function plain<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(url, init);
  const text = await res.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    /* not json */
  }
  if (!res.ok) throw new Error((json as { error?: string } | null)?.error ?? `Request failed (${res.status})`);
  return json as T;
}

/** The public sign-up pages: no sign-in, no cookies. */
export const publicApi = {
  info: (code: string) => plain<PublicRegisterInfo>('GET', `/api/public/register/${encodeURIComponent(code)}`),
  submit: (code: string, body: unknown) => plain<{ status: 'registered' | 'pending'; displayName: string; joinUrl?: string }>('POST', `/api/public/register/${encodeURIComponent(code)}`, body),
};

export class ApiClient {
  constructor(private readonly base = '/api', private readonly leaseToken: () => string | null = () => null) {}

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...zoomContextHeaders() };
    const lease = this.leaseToken();
    if (lease) headers['X-Lease-Token'] = lease;
    const init: RequestInit = { method, headers, credentials: 'same-origin' };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetch(`${this.base}${path}`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${method} ${path} -> ${res.status}: ${text}`);
    }
    return (await res.json()) as T;
  }

  listEvents() {
    return this.req<{ events: { eventId: string; title: string; state: string; updatedAt: string }[] }>('GET', '/events');
  }
  createEvent(title: string, timezone: string) {
    return this.req<StoredEvent>('POST', '/events', { title, timezone });
  }
  getEvent(eventId: string) {
    return this.req<StoredEvent>('GET', `/events/${encodeURIComponent(eventId)}`);
  }
  saveEvent(eventId: string, revision: number, record: EventRecord) {
    return this.req<StoredEvent>('PUT', `/events/${encodeURIComponent(eventId)}`, { revision, record });
  }
  acquireLease(eventId: string, take: boolean, token?: string | null) {
    return this.req<{ token: string; expiresAt: string; holder: string }>('POST', `/events/${encodeURIComponent(eventId)}/lease`, token ? { take, token } : { take });
  }
  releaseLease(eventId: string) {
    return this.req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(eventId)}/lease`);
  }
  operatorEvents() {
    return this.req<{ events: { eventId: string; title: string; state: string; updatedAt: string }[] }>('GET', '/operator/events');
  }
  redeem(code: string) {
    return this.req<{ eventId: string }>('POST', '/operator/redeem', { code });
  }
  deleteEvent(eventId: string) {
    return this.req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(eventId)}`);
  }
  createCode(eventId: string) {
    return this.req<{ id: string; code: string; expiresAt: string }>('POST', `/events/${encodeURIComponent(eventId)}/codes`);
  }
  access(eventId: string) {
    return this.req<AccessInfo>('GET', `/events/${encodeURIComponent(eventId)}/access`);
  }
  revokeCode(eventId: string, codeId: string) {
    return this.req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(eventId)}/codes/${encodeURIComponent(codeId)}`);
  }
  revokeOperator(eventId: string, zoomUserId: string) {
    return this.req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(eventId)}/operators/${encodeURIComponent(zoomUserId)}`);
  }
  zoomStatus() {
    return this.req<{ configured: boolean; ok: boolean; detail: string; fake?: boolean }>('GET', '/zoom/status');
  }
  getSetup(eventId: string) {
    return this.req<SetupResponse>('GET', `/events/${encodeURIComponent(eventId)}/setup`);
  }
  saveSetup(eventId: string, patch: Partial<EventSetup>) {
    return this.req<{ setup: EventSetup }>('PUT', `/events/${encodeURIComponent(eventId)}/setup`, patch);
  }
  createMeeting(eventId: string) {
    return this.req<{ setup: EventSetup }>('POST', `/events/${encodeURIComponent(eventId)}/meeting`);
  }
  rotateLink(eventId: string, kind: 'student' | 'speaker') {
    return this.req<{ links: { student: string; speaker: string } }>('POST', `/events/${encodeURIComponent(eventId)}/links/${kind}/rotate`);
  }
  registrations(eventId: string) {
    return this.req<{ registrations: RegistrationRow[] }>('GET', `/events/${encodeURIComponent(eventId)}/registrations`);
  }
  updateRegistration(eventId: string, id: string, patch: { action?: 'approve' | 'reject'; roomId?: string | null }) {
    return this.req<{ registration: RegistrationRow }>('PATCH', `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(id)}`, patch);
  }
  removeRegistration(eventId: string, id: string) {
    return this.req<{ ok: true }>('DELETE', `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(id)}`);
  }
  generateRota(eventId: string) {
    return this.req<RotaPreview>('POST', `/events/${encodeURIComponent(eventId)}/rota/generate`);
  }
  exportUrl(eventId: string) {
    return `${this.base}/events/${encodeURIComponent(eventId)}/export`;
  }
}
